import React from 'react';
import PropTypes from 'prop-types';
import withData from '@bd/bdcore-data';
import { get } from 'lodash';
import { Image } from '@bd/bdcore-utilities';
import { css } from '@bd/bdui-styles/emotion';
import { Link } from '@bd/bdcore-named-routes';
import numeral from 'numeral';
import { colors } from '../../styles';
import DetailHeaderWrapper from '../../components/DetailHeaderWrapper';
import ContactModal from '../../components/ContactModal';
import MortgageCalculatorModal from '../../components/MortgageCalculatorModal';

const styles = css`
  width: 100%;
  flex-direction: column;
  padding-top: 70px;
  @media(min-width: 992px) {
    display: flex;
    height: 800px;
    flex-direction: row;
    padding-top: 105px; /* for header area */
  }
  .DetailHeader_imageWrapper {
    width: 100%;
    height: 33vh;
    @media(min-width: 992px) {
      order: 1;
      flex: 1;
      height: 100%;
    }
  }
  .DetailHeader_contentWrapper {
    background-color: ${colors.neutral};
    @media(min-width: 992px) {
      width: 33.333vw;
      padding: 90px 0 0 0; /* for descending logo */
    }
  }
  .DetailHeader_heading {
    background-color: ${colors.neutralDark};
    font-weight: 700;
    font-size: 20px;
    color: ${colors.brand1};
    margin: 0;
    padding: 20px 15px;
    line-height: 18px;
    @media(min-width: 992px) {
      font-size: 26px;
      padding: 20px 50px;
    }
    span {
      font-weight: 700;
      font-size: 14px;
      letter-spacing: 0.5px;
      color: ${colors.body};
    }
  }
  .DetailHeader_price {
    background-color: ${colors.white};
    margin: 0;
    font-weight: 700;
    font-size: 24px;
    letter-spacing: 0.5px;
    padding: 5px 15px 10px 15px;
    color: ${colors.body};
    @media(min-width: 992px) {
      padding: 5px 10px 10px 50px;
    }
    span {
      font-weight: 700;
      font-size: 14px;
      letter-spacing: 0.5px;
    }
  }
  .DetailHeader_detailsList {
    list-style: none;
    margin: 15px 0 15px 0;
    @media(min-width: 992px) {
      padding: 0 50px;
    }
  }
  .DetailHeader_detailsListItem {
    font-size: 16px;
    line-height: 1.2;
    text-align: left;
    color: ${colors.body};
    font-weight: 300;
    margin: 0 0 6px 0;
  }
  .DetailHeader_iconList {
    display: flex;
    list-style: none;
    margin: 0;
    border-top: 1px solid ${colors.neutralDarkest};
    border-bottom: 1px solid ${colors.neutralDarkest};
    align-items: flex-start;
    padding: 0 50px;
    li {
      text-align: center;
      flex: 1;
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      border-right: 1px solid ${colors.neutralDarkest};
      padding: 10px 5px;
      font-size: 15px;
      line-height: 1;
      &:first-child {
        border-left: 1px solid ${colors.neutralDarkest};
      }
      span {
        display: block;
      }
      img {
        height: 20px;
        margin: 0 0 5px 0;
      }
    }
  }
  .DetailHeader_links {
    padding: 10px 50px;
    display: flex;
    flex-direction: column;
    font-weight: 700;
    font-size: 14px;
    color: ${colors.brand1};
    text-transform: uppercase;
    @media(min-width: 992px) {
      padding: 10px 50px;
      flex-direction: row;
    }
    a, span {
      font-weight: bold;
      font-size: 14px;
      color: ${colors.brand1};
      letter-spacing: 1.85px;
      margin-bottom: 10px;
      text-transform: uppercase;
      margin: 10px;
      img {
        margin-right: 5px;
        height: 20px;
      }
    }
  }
  .DetailHeader_button {
    background-color: ${colors.brand1};
    padding: 0 30px 0 80px;
    color: ${colors.white};
    font-weight: 400;
    position: relative;
    line-height: 60px;
    display: inline-block;
    margin: 20px 0 20px 15px;
    transition: all .3s ease;
    cursor: pointer;
    &:hover {
      background-color: ${colors.brand1Lighter};
    }
    @media(min-width: 992px) {
      margin: 0 0 20px 50px;
    }
  }
  .DetailHeader_buttonImage {
    position: absolute;
    width: 60px;
    height: 60px;
    left: 0;
    top: 0;
    object-fit: cover;
  }
`;

function PlanHeader({ community, plan, builder, agent }) {
  const beds = plan.beds_range ? plan.beds_range : plan.beds;
  const bathsFull = plan.baths_range ? plan.baths_range : plan.bathsFull;
  return (
    <div className={styles}>
      <DetailHeaderWrapper imageUrl={plan.photos.length ? get(plan, 'photos[0].contentUrl') : get(plan, 'elevationPhotos[0].contentUrl')} className="DetailHeader_imageWrapper" />
      <div className="DetailHeader_contentWrapper">
        <h1 className="DetailHeader_heading">{get(plan, 'name')} - Floor Plan</h1>
        <h3 className="DetailHeader_price">
          <span>Priced At</span><br />
          {numeral(plan.price).format('$0,0')}
        </h3>
        <ul className="DetailHeader_detailsList">
          {
            get(community, 'name') &&
            <li className="PlanCommunity_name">
              <b>Community: </b><Link name="community-detail" community={community}>{community.name}</Link>
            </li>
          }
          {/* {
            get(plan, 'plan_features') &&
            <div className="PlanCommunity_features">
              <span><b>Highlighted Features:</b></span>
              {
                get(plan, 'plan_features').join(', ')
              }
            </div>
          } */}
        </ul>
        <ul className="DetailHeader_iconList">
          {
            beds &&
            <li>
              <img src="/images/icon-beds.svg" alt="Icon Beds" />
              <span><b>{beds}</b></span>
              <span>Beds</span>
            </li>
          }
          {
            bathsFull &&
            <li>
              <img src="/images/icon-baths.svg" alt="Icon Baths" />
              <span><b>{bathsFull} {get(plan, 'bathsHalf', 0) > 0 && !plan.baths_range && '.5'}</b></span>
              <span>Baths</span>
            </li>
          }
          {
            plan.sqft &&
            <li>
              <img src="/images/icon-sqft.svg" alt="Icon Sqft" />
              <span><b>{numeral(plan.sqft).format('0,0')}</b></span>
              <span>SQFT</span>
            </li>
          }
          {
            plan.garages &&
            <li>
              <img src="/images/icon-garages.svg" alt="Icon Garage" />
              <span><b>{plan.garages}</b></span>
              <span>Cars</span>
            </li>
          }
          {/* {
            priceSeries.length > 0 &&
            priceSeries.map(item => (
              <li key={item.name} >
                <img src="/images/icon-garage.svg" alt="Icon Garage" />
                <span className="HomeCard_iconListValue"><b>{item.garages}</b></span>
                <span className="HomeCard_iconListValue">Cars</span>
              </li>
            ))
          } */}
        </ul>
        <div className="DetailHeader_links">
          {
            plan.price > 0 &&
            <MortgageCalculatorModal price={plan.price}><img src="/images/icon-calculator.svg" alt="Icon Get Directions" /> CALCULATE PAYMENT</MortgageCalculatorModal>
          }
          {
            plan.brochure &&
            <a href={plan.brochure} target="_blank" rel="noopener noreferrer"><img src="/images/icon-pdf.svg" alt="Icon Get Directions" /> Download PDF</a>
          }
        </div>
        <div className="mt-3">
          <ContactModal builder={builder} formName="contactUsModalCommunity">
            <div className="DetailHeader_button">
              <Image
                src={get(agent, 'logo.contentUrl')}
                alt="Community Agent"
                className="DetailHeader_buttonImage"
                width={100}
                height={100}
              />
              Schedule A Showing
            </div>
          </ContactModal>
        </div>
      </div>
    </div>
  );
}

PlanHeader.propTypes = {
  community: PropTypes.object,
  plan: PropTypes.object,
  builder: PropTypes.object.isRequired,
  agent: PropTypes.object,
};

PlanHeader.defaultProps = {
  community: undefined,
  plan: undefined,
  agent: undefined,
};

export default withData(
  {
    community: true,
    plan: true,
    builder: true,
    agent: true,
  },
  {
    required: {
      plan: true,
    },
  },
)(PlanHeader);



// WEBPACK FOOTER //
// ./src/pages/PlanDetail/PlanHeader.js